import { CHAT_ACCESS_MESSAGES, notifyChatAccessError } from "../utils/chatAccess";
import { createResponseRequestKeys } from "../utils/responseRequestKey";
import {prepareNativeNotificationRead} from "../utils/nativeBadge";
import { DWORKS_API_BASE, FORCE_DISPATCH_FAILURE_FOR_TEST } from "../constants/dworksConfig";
import { createHttpError, toAppError } from "../utils/appError";
import { reportClientError } from "../utils/errorReporter";

const inFlightWriteRequests = new Set();
let vendorResponseRequestKeys;

export function fetchBuyerVendorFeedback(token, orderIds) {
  // POST carries a bounded read-only batch, so it must not use the write guard.
  return requestDworksApi("/api/buyer/vendor-feedback", {
    method: "POST", token, payload: { order_ids: orderIds }
  });
}

function buildWriteRequestKey(method, path, payload) {
  let payloadKey = "";
  try {
    payloadKey = JSON.stringify(payload ?? null);
  } catch {
    payloadKey = String(payload ?? "");
  }
  return `${String(method || "POST").toUpperCase()}:${path}:${payloadKey}`;
}

async function runSingleWrite(method, path, payload, options = {}) {
  const requestKey = buildWriteRequestKey(method, path, payload);
  if (inFlightWriteRequests.has(requestKey)) {
    const error = new Error("동일한 요청을 처리 중입니다. 완료될 때까지 잠시 기다려 주세요.");
    error.code = "DUPLICATE_WRITE_IN_FLIGHT";
    throw error;
  }

  inFlightWriteRequests.add(requestKey);
  try {
    return await requestDworksApi(path, {
      method,
      payload,
      token: options.token,
      preferErrorDetail: options.preferErrorDetail
    });
  } finally {
    inFlightWriteRequests.delete(requestKey);
  }
}

async function requestDworksApi(path, options = {}) {
  const {
    method = "GET",
    payload = null,
    token = "",
    preferErrorDetail = false
  } = options;

  const requestId =
    globalThis.crypto?.randomUUID?.() ||
    `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  const headers = {
    "Content-Type": "application/json",
    "X-Request-Id": requestId
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const finishNativeRead = prepareNativeNotificationRead(path, method);
  let response;
  const controller = new AbortController();
  const timeout = (path === "/api/buyer/vendor-feedback" || (method === "GET" && /\/(?:chats|factory-chat\/threads|chat\/threads|broadcast-chat)(?:\/|\?|$)/.test(path)))
    ? setTimeout(() => controller.abort(), 15000) : null;
  try {
    response = await fetch(`${DWORKS_API_BASE}${path}`, {
      method,
      headers,
      signal: controller.signal,
      body: payload === null || payload === undefined ? undefined : JSON.stringify(payload)
    });
  } catch (error) {
    if (timeout) clearTimeout(timeout);
    const appError = toAppError(error, {
      requestId,
      fallback: "서버에 연결하지 못했습니다. 인터넷 연결을 확인한 후 다시 시도해 주세요."
    });
    reportClientError(appError, { source: "api", route: path, requestId });
    throw appError;
  }

  let responseData = null;
  try {
    responseData = await response.json();
  } catch (error) {
    if (controller.signal.aborted) throw new Error("응답 시간이 초과됐습니다. 다시 시도해 주세요.");
    responseData = null;
  } finally {
    if (timeout) clearTimeout(timeout);
  }

  if (!response.ok || responseData?.ok === false) {
    const vendorPortalMeError = path === "/api/vendor/portal/me" && [
      "VENDOR_PORTAL_ACCOUNT_INACTIVE", "VENDOR_PORTAL_MEMBERSHIP_INACTIVE",
      "VENDOR_PORTAL_MEMBERSHIP_AMBIGUOUS", "VENDOR_PORTAL_ACCOUNT_LOOKUP_FAILED",
      "VENDOR_PORTAL_ACCOUNT_LINK_CHANGED"
    ].includes(responseData?.code);
    const chatAccessError = ["CHAT_CONNECTION_INACTIVE","CHAT_COUNTERPART_INACTIVE","CHAT_THREAD_NOT_FOUND","CHAT_ACCESS_LOOKUP_FAILED"].includes(responseData?.code);
    const detail = preferErrorDetail || vendorPortalMeError || chatAccessError
      ? responseData?.error || responseData?.message
      : responseData?.message || responseData?.error;

    const appError = createHttpError(
      response,
      detail ? { ...responseData, message: detail } : responseData,
      "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요."
    );
    if (vendorPortalMeError) {
      appError.code = responseData.code;
      appError.message = detail || appError.message;
    }
    if (chatAccessError) {
      appError.code=responseData.code;appError.message=CHAT_ACCESS_MESSAGES[responseData.code];
      notifyChatAccessError(path,appError);
    }
    reportClientError(appError, { source: "api", route: path, requestId });
    throw appError;
  }

  finishNativeRead(responseData);
  return responseData;
}

async function postDworksApi(path, payload, options = {}) {
  return runSingleWrite("POST", path, payload, options);
}

export async function getDworksApi(path, options = {}) {
  return requestDworksApi(path, {
    method: "GET",
    token: options.token,
    preferErrorDetail: options.preferErrorDetail
  });
}

async function patchDworksApi(path, payload, options = {}) {
  return runSingleWrite("PATCH", path, payload, options);
}

async function deleteDworksApi(path, options = {}) {
  return runSingleWrite("DELETE", path, null, options);
}

export async function checkSignupPhoneAvailability(phone, accountType = "buyer") {
  return postDworksApi("/api/auth/signup/phone-availability", {
    phone,
    account_type: accountType
  }, { preferErrorDetail: true });
}

export async function createBuyerPaymentOrder(token, { planCode, upgradeRequestId }) {
  if (!token) throw Object.assign(new Error("다시 로그인해 주세요."), { status: 401 });
  const data = await postDworksApi("/api/buyer/payment-orders", {
    plan_code: planCode, upgrade_request_id: upgradeRequestId
  }, { token, preferErrorDetail: true });
  const order = data?.payment_order;
  if (data?.ok !== true || !order || order.plan_code !== planCode || order.status !== "pending" ||
      !Number.isSafeInteger(order.amount) || order.amount <= 0 ||
      typeof order.plan_name !== "string" || !order.plan_name ||
      typeof order.provider_order_id !== "string" || !/^[A-Za-z0-9_-]{6,64}$/.test(order.provider_order_id)) {
    throw new Error("결제 주문 응답을 확인할 수 없습니다.");
  }
  return order;
}

export async function fetchBuyerPaymentOrders(token, { limit = 8, providerOrderId } = {}) {
  if (!token) {
    throw Object.assign(new Error("다시 로그인해 주세요."), { status: 401 });
  }
  const params = new URLSearchParams({ limit: String(limit) });
  if (providerOrderId !== undefined) params.set("provider_order_id", providerOrderId);
  const data = await getDworksApi(`/api/buyer/payment-orders?${params}`, {
    token, preferErrorDetail: true
  });
  if (data?.ok !== true || !Array.isArray(data.payment_orders)) {
    throw new Error("결제 내역 응답을 확인할 수 없습니다.");
  }
  return data.payment_orders;
}

export async function fetchAdminPaymentOrdersSummary(token, { view, monthStart } = {}) {
  if (!token) throw Object.assign(new Error("다시 로그인해 주세요."), { status: 401 });
  if (!["v1", "v2"].includes(view) || typeof monthStart !== "string") {
    throw new Error("결제 통계 조회 조건을 확인해주세요.");
  }
  const params = new URLSearchParams({ view, month_start: monthStart });
  const data = await getDworksApi(`/admin/payment-orders/summary?${params}`, { token, preferErrorDetail: true });
  const limit = view === "v1" ? 300 : 500;
  if (data?.ok !== true || data.view !== view || data.month_start !== monthStart || data.sample_limit !== limit ||
      !Number.isSafeInteger(data.sampled_count) || data.sampled_count < 0 || data.sampled_count > limit ||
      ![data.paid_count, data.failure_count].every(n => Number.isSafeInteger(n) && n >= 0 && n <= data.sampled_count) ||
      data.paid_count + data.failure_count > data.sampled_count || !Number.isFinite(data.month_paid_amount) ||
      (view === "v1" && (!Array.isArray(data.recent_orders) || !Array.isArray(data.recent_failures) ||
        data.recent_orders.length > 6 || data.recent_failures.length > 8 ||
        ![...data.recent_orders, ...data.recent_failures].every(row => row && typeof row === "object" && !Array.isArray(row))))) {
    throw new Error("결제 통계 응답을 확인할 수 없습니다.");
  }
  return data;
}

// OPS dispatch statistics are server-authorized; never fall back to raw table reads.
export async function fetchAdminDispatchLogsSummary(token) {
  if (!token) {
    throw Object.assign(new Error("다시 로그인해 주세요."), { status: 401 });
  }
  const data = await getDworksApi(
    "/admin/dispatch-logs/summary?days=7&detail_limit=8",
    { token, preferErrorDetail: true }
  );
  if (data?.ok !== true || data.period_days !== 7 ||
      !Number.isSafeInteger(data.success_count) || data.success_count < 0 ||
      !Number.isSafeInteger(data.failure_count) || data.failure_count < 0 ||
      !Array.isArray(data.recent_failures)) {
    throw new Error("발송 로그 요약 응답을 확인할 수 없습니다. 다시 시도해 주세요.");
  }
  return data;
}

export async function sendDispatchRequest(token, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  if (FORCE_DISPATCH_FAILURE_FOR_TEST) {
    throw new Error("테스트용 전달 실패");
  }

  return postDworksApi("/dispatch/send", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function sendSupplierOrderAlimtalk(token, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  return postDworksApi("/api/kakao-alimtalk/supplier-order", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function sendFactoryOrderAlimtalk(token, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  return postDworksApi("/api/kakao-alimtalk/factory-order", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function sendSignupCompleteAlimtalk(token, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  return postDworksApi("/api/kakao-alimtalk/signup-complete", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function sendPushRequest(token, payload) {
  return postDworksApi("/push/send", payload, { token });
}

export async function fetchVendorConfirmOrder(token) {
  const safeToken = encodeURIComponent(String(token || "").trim());
  if (!safeToken) {
    throw new Error("발주 확인 토큰이 없습니다.");
  }

  return getDworksApi(`/api/vendor/confirm/${safeToken}`);
}

export async function submitVendorConfirmResponse(token, payload) {
  const safeToken = encodeURIComponent(String(token || "").trim());
  if (!safeToken) {
    throw new Error("발주 확인 토큰이 없습니다.");
  }

  return postDworksApi(`/api/vendor/confirm/${safeToken}/respond`, payload);
}

export async function createBuyerVendorInvite(token, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  return postDworksApi("/api/buyer/vendor-invites", payload, { token, preferErrorDetail: true });
}

export async function fetchBuyerAccountLinkCandidates(token, kind) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  if (!["vendor", "factory"].includes(kind)) throw new Error("연결 후보 유형을 확인해 주세요.");
  return getDworksApi(`/api/buyer/account-link-candidates?kind=${kind}`, { token });
}

export async function fetchAdminAccountLinkReview(token) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  return getDworksApi("/admin/account-link-candidates/summary", { token });
}

export async function createVendorPortalInviteFromConfirm(confirmToken) {
  const safeToken = encodeURIComponent(String(confirmToken || "").trim());
  if (!safeToken) {
    throw new Error("발주 확인 토큰이 없습니다.");
  }

  return postDworksApi(`/api/vendor/invites/from-confirm/${safeToken}`, {}, { preferErrorDetail: true });
}

export async function fetchVendorPortalInvite(inviteToken) {
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) {
    throw new Error("공급처 가입 초대 토큰이 없습니다.");
  }

  return getDworksApi(`/api/vendor/invites/${safeToken}`, { preferErrorDetail: true });
}

export async function joinVendorPortalInvite(inviteToken, payload) {
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) {
    throw new Error("공급처 가입 초대 토큰이 없습니다.");
  }

  return postDworksApi(`/api/vendor/invites/${safeToken}/join`, payload, { preferErrorDetail: true });
}

export async function linkExistingVendorPortalInvite(token, inviteToken) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) throw new Error("공급처 가입 초대 토큰이 없습니다.");
  return postDworksApi(`/api/vendor/invites/${safeToken}/link`, {}, {
    token,
    preferErrorDetail: true
  });
}

export async function signupVendorPortal(payload) {
  return postDworksApi("/api/vendor/signup", payload, { preferErrorDetail: true });
}

export async function fetchBuyerVendorUnitPrices(token, orderId) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  const safeOrderId = encodeURIComponent(String(orderId || "").trim());
  if (!safeOrderId) {
    throw new Error("발주 ID가 없습니다.");
  }

  return getDworksApi(`/api/buyer/orders/${safeOrderId}/vendor-unit-prices`, {
    token,
    preferErrorDetail: true
  });
}

export async function decideBuyerVendorUnitPrice(token, unitPriceId, payload) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  const safeUnitPriceId = encodeURIComponent(String(unitPriceId || "").trim());
  if (!safeUnitPriceId) {
    throw new Error("처리할 단가 제안 정보가 없습니다.");
  }

  return postDworksApi(`/api/buyer/unit-prices/${safeUnitPriceId}/decide`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchBuyerVendorShipments(token, orderId) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }

  const safeOrderId = encodeURIComponent(String(orderId || "").trim());
  if (!safeOrderId) {
    throw new Error("발주 ID가 없습니다.");
  }

  return getDworksApi(`/api/buyer/orders/${safeOrderId}/vendor-shipments`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalUnitPrices(token, tokenId) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  return getDworksApi(`/api/vendor/portal/orders/${safeTokenId}/unit-prices`, {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalUnitPriceProposal(token, tokenId, payload) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  return postDworksApi(`/api/vendor/portal/orders/${safeTokenId}/unit-prices`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalShipments(token, tokenId) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  return getDworksApi(`/api/vendor/portal/orders/${safeTokenId}/shipments`, { token });
}

export async function createVendorPortalShipment(token, tokenId, payload) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  return postDworksApi(`/api/vendor/portal/orders/${safeTokenId}/shipments`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function cancelVendorPortalShipment(token, tokenId, shipmentId, payload) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  const safeShipmentId = encodeURIComponent(String(shipmentId || "").trim());

  if (!safeTokenId || !safeShipmentId) {
    throw new Error("취소할 출고 정보를 확인하지 못했습니다.");
  }

  return postDworksApi(
    `/api/vendor/portal/orders/${safeTokenId}/shipments/${safeShipmentId}/cancel`,
    payload,
    {
      token,
      preferErrorDetail: true
    }
  );
}

export async function fetchVendorPortalMe(token) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  return getDworksApi("/api/vendor/portal/me", { token });
}

export async function updateVendorPortalAccount(token, payload) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  return patchDworksApi("/api/vendor/portal/account", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalStaff(token) {
  if (!token) throw new Error("공급처 로그인 정보가 없습니다.");
  return getDworksApi("/api/vendor/portal/staff", { token, preferErrorDetail: true });
}

export async function createVendorPortalStaffInvite(token, payload) {
  if (!token) throw new Error("공급처 로그인 정보가 없습니다.");
  return postDworksApi("/api/vendor/portal/staff/invites", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function resendVendorPortalStaffInvite(token, inviteId) {
  if (!token) throw new Error("공급처 로그인 정보가 없습니다.");
  const safeId = encodeURIComponent(String(inviteId || "").trim());
  if (!safeId) throw new Error("재발송할 직원 초대가 없습니다.");
  return postDworksApi(`/api/vendor/portal/staff/invites/${safeId}/resend`, {}, {
    token,
    preferErrorDetail: true
  });
}

export async function cancelVendorPortalStaffInvite(token, inviteId) {
  if (!token) throw new Error("공급처 로그인 정보가 없습니다.");
  const safeId = encodeURIComponent(String(inviteId || "").trim());
  if (!safeId) throw new Error("취소할 직원 초대가 없습니다.");
  return deleteDworksApi(`/api/vendor/portal/staff/invites/${safeId}`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorStaffInvite(inviteToken) {
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) throw new Error("직원 초대 정보가 없습니다.");
  return getDworksApi(`/api/vendor/staff-invites/${safeToken}`, { preferErrorDetail: true });
}

export async function joinVendorStaffInvite(inviteToken, payload) {
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) throw new Error("직원 초대 정보가 없습니다.");
  return postDworksApi(`/api/vendor/staff-invites/${safeToken}/join`, payload, {
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalOrders(token, options = {}) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const params = new URLSearchParams();
  params.set("limit", String(options.limit || 30));
  params.set("offset", String(Math.max(Number(options.offset) || 0, 0)));
  const q = String(options.q || "").trim();
  if (q) params.set("q", q);
  // 이 목록만 요청마다 고유한 쿼리를 붙여 브라우저 캐시를 피합니다.
  // 로그인/계정 확인 API에는 영향을 주지 않습니다.
  params.set("__dworks_live", String(Date.now()));
  return getDworksApi(`/api/vendor/portal/orders?${params.toString()}`, { token });
}

export async function fetchVendorPortalOrderDetail(token, tokenId) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  return getDworksApi(`/api/vendor/portal/orders/${safeTokenId}`, { token });
}

export async function submitVendorPortalResponse(token, tokenId, payload) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }

  const safeTokenId = encodeURIComponent(String(tokenId || "").trim());
  if (!safeTokenId) {
    throw new Error("발주 확인 ID가 없습니다.");
  }

  vendorResponseRequestKeys ||= createResponseRequestKeys();
  const request=vendorResponseRequestKeys.get(`${token}:${safeTokenId}`,payload);
  const result=await postDworksApi(`/api/vendor/portal/orders/${safeTokenId}/respond`, {...payload,request_key:request.key}, { token });
  vendorResponseRequestKeys.complete(request.fingerprint);
  return result;
}

// Supplier Mini ERP API — v71-3-2
function requireVendorPortalToken(token) {
  if (!token) {
    throw new Error("공급처 로그인 정보가 없습니다.");
  }
}

function encodeRequiredVendorPortalId(value, message) {
  const safeValue = encodeURIComponent(String(value || "").trim());
  if (!safeValue) {
    throw new Error(message);
  }
  return safeValue;
}

export async function fetchVendorPortalErpLocations(token) {
  requireVendorPortalToken(token);
  return getDworksApi("/api/vendor/portal/erp/locations", {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpLocation(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/locations", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function updateVendorPortalErpLocation(token, locationId, payload) {
  requireVendorPortalToken(token);
  const safeLocationId = encodeRequiredVendorPortalId(locationId, "수정할 창고/매장 정보가 없습니다.");
  return requestDworksApi(`/api/vendor/portal/erp/locations/${safeLocationId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpItems(token, options = {}) {
  requireVendorPortalToken(token);
  const params = new URLSearchParams();
  params.set("limit", String(options.limit || (options.view === "options" ? 1000 : 30)));
  params.set("offset", String(Math.max(Number(options.offset) || 0, 0)));
  if (options.view) params.set("view", String(options.view));
  if (options.q) params.set("q", String(options.q).trim());
  if (options.itemType || options.item_type) params.set("item_type", String(options.itemType || options.item_type));
  if (options.active === true || options.active === false) params.set("active", String(options.active));
  return getDworksApi(`/api/vendor/portal/erp/items?${params.toString()}`, {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpItem(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/items", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function updateVendorPortalErpItem(token, itemId, payload) {
  requireVendorPortalToken(token);
  const safeItemId = encodeRequiredVendorPortalId(itemId, "수정할 재고 품목 정보가 없습니다.");
  return requestDworksApi(`/api/vendor/portal/erp/items/${safeItemId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpItemChangeLogs(token, itemId) {
  requireVendorPortalToken(token);
  const safeItemId = encodeRequiredVendorPortalId(itemId, "수정 이력을 조회할 재고 품목 정보가 없습니다.");
  return getDworksApi(`/api/vendor/portal/erp/items/${safeItemId}/change-logs`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpStocks(token, options = {}) {
  requireVendorPortalToken(token);
  const params = new URLSearchParams();
  if (options.view) params.set("view", String(options.view).trim());
  if (options.locationId || options.location_id) params.set("location_id", String(options.locationId || options.location_id));
  if (options.itemId || options.item_id) params.set("item_id", String(options.itemId || options.item_id));
  return getDworksApi(`/api/vendor/portal/erp/stocks?${params.toString()}`, {
    token,
    preferErrorDetail: true
  });
}

export async function adjustVendorPortalErpStock(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/stocks/adjust", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpTransfers(token, options = {}) {
  requireVendorPortalToken(token);
  const params = new URLSearchParams();
  params.set("limit", String(options.limit || 30));
  params.set("offset", String(Math.max(Number(options.offset) || 0, 0)));
  if (options.status) params.set("status", String(options.status).trim());
  return getDworksApi(`/api/vendor/portal/erp/transfers?${params.toString()}`, {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpTransfer(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/transfers", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function shipVendorPortalErpTransfer(token, transferId, payload = {}) {
  requireVendorPortalToken(token);
  const safeTransferId = encodeRequiredVendorPortalId(transferId, "출고 처리할 재고 이동 정보가 없습니다.");
  return postDworksApi(`/api/vendor/portal/erp/transfers/${safeTransferId}/ship`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function receiveVendorPortalErpTransfer(token, transferId, payload = {}) {
  requireVendorPortalToken(token);
  const safeTransferId = encodeRequiredVendorPortalId(transferId, "입고 확인할 재고 이동 정보가 없습니다.");
  return postDworksApi(`/api/vendor/portal/erp/transfers/${safeTransferId}/receive`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function cancelVendorPortalErpTransfer(token, transferId, payload = {}) {
  requireVendorPortalToken(token);
  const safeTransferId = encodeRequiredVendorPortalId(transferId, "취소할 재고 이동 정보가 없습니다.");
  return postDworksApi(`/api/vendor/portal/erp/transfers/${safeTransferId}/cancel`, payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpMovements(token, options = {}) {
  requireVendorPortalToken(token);
  const params = new URLSearchParams();
  params.set("limit", String(options.limit || 30));
  params.set("offset", String(Math.max(Number(options.offset) || 0, 0)));
  const q = String(options.q || "").trim();
  if (q) params.set("q", q);
  if (options.itemId || options.item_id) params.set("item_id", String(options.itemId || options.item_id));
  if (options.locationId || options.location_id) params.set("location_id", String(options.locationId || options.location_id));
  if (options.movementType || options.movement_type) params.set("movement_type", String(options.movementType || options.movement_type));
  return getDworksApi(`/api/vendor/portal/erp/movements?${params.toString()}`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpPrices(token) {
  requireVendorPortalToken(token);
  return getDworksApi("/api/vendor/portal/erp/prices", {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpPrice(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/prices", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function updateVendorPortalErpPrice(token, priceRuleId, payload) {
  requireVendorPortalToken(token);
  const safePriceRuleId = encodeURIComponent(String(priceRuleId || "").trim());
  if (!safePriceRuleId) {
    throw new Error("수정할 단가 정보가 없습니다.");
  }

  return requestDworksApi(`/api/vendor/portal/erp/prices/${safePriceRuleId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function deleteVendorPortalErpPrice(token, priceRuleId) {
  requireVendorPortalToken(token);
  const safePriceRuleId = encodeURIComponent(String(priceRuleId || "").trim());
  if (!safePriceRuleId) {
    throw new Error("삭제할 단가 정보가 없습니다.");
  }

  return requestDworksApi(`/api/vendor/portal/erp/prices/${safePriceRuleId}`, {
    method: "DELETE",
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpSettlements(token) {
  requireVendorPortalToken(token);
  return getDworksApi("/api/vendor/portal/erp/settlements", {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpSettlementSnapshot(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/settlement-snapshots", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalErpPayments(token) {
  requireVendorPortalToken(token);
  return getDworksApi("/api/vendor/portal/erp/payments", {
    token,
    preferErrorDetail: true
  });
}

export async function createVendorPortalErpPayment(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/payments", payload, {
    token,
    preferErrorDetail: true
  });
}
// =========================================================
// D-Works v72-r3 Direct Vendor Chat API — Buyer
// 자유채팅: 발주자 ↔ 연결된 공급처
// =========================================================

function requireBuyerChatToken(token) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }
}

function encodeRequiredBuyerChatId(value, message) {
  const safeValue = encodeURIComponent(String(value || "").trim());

  if (!safeValue) {
    throw new Error(message);
  }

  return safeValue;
}

// 채팅 가능한 공급처 목록
export async function fetchBuyerChatContacts(token) {
  requireBuyerChatToken(token);

  return getDworksApi("/api/buyer/chats/contacts", {
    token,
    preferErrorDetail: true
  });
}

// 발주자의 자유채팅방 목록
export async function fetchBuyerChats(token) {
  requireBuyerChatToken(token);

  return getDworksApi("/api/buyer/chats", {
    token,
    preferErrorDetail: true
  });
}

// 공급처와 자유채팅방 생성 또는 기존 방 반환
export async function createBuyerVendorChat(token, vendorAccountId) {
  requireBuyerChatToken(token);

  const safeVendorAccountId = encodeRequiredBuyerChatId(
    vendorAccountId,
    "채팅할 공급처 정보가 없습니다."
  );

  return postDworksApi(
    `/api/buyer/chats/vendor/${safeVendorAccountId}`,
    {},
    {
      token,
      preferErrorDetail: true
    }
  );
}

// 자유채팅방 조회
// 현재 서버에서는 채팅방 조회 시 발주자 읽음 처리도 함께 수행됩니다.
export async function fetchBuyerChat(token, threadId, limit=50) {
  requireBuyerChatToken(token);

  const safeThreadId = encodeRequiredBuyerChatId(
    threadId,
    "채팅방 정보가 없습니다."
  );

  return getDworksApi(
    `/api/buyer/chats/${safeThreadId}?limit=${limit}`,
    {
      token,
      preferErrorDetail: true
    }
  );
}

// 자유채팅 메시지 발송
export async function sendBuyerChatMessage(token, threadId, payload) {
  requireBuyerChatToken(token);

  const safeThreadId = encodeRequiredBuyerChatId(
    threadId,
    "채팅방 정보가 없습니다."
  );

  return postDworksApi(
    `/api/buyer/chats/${safeThreadId}/messages`,
    payload,
    {
      token,
      preferErrorDetail: true
    }
  );
}

// 자유채팅 읽음 처리
export async function markBuyerChatRead(token, threadId) {
  requireBuyerChatToken(token);

  const safeThreadId = encodeRequiredBuyerChatId(
    threadId,
    "채팅방 정보가 없습니다."
  );

  return postDworksApi(
    `/api/buyer/chats/${safeThreadId}/read`,
    {},
    {
      token,
      preferErrorDetail: true
    }
  );
}

export async function previewVendorPortalExcelImport(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/excel-import/preview", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchVendorPortalExcelImports(token, options = {}) {
  requireVendorPortalToken(token);
  const params = new URLSearchParams();
  if (options.status) params.set("status", options.status);
  if (options.import_type) params.set("import_type", options.import_type);
  if (options.limit) params.set("limit", String(options.limit));
  const query = params.toString();
  return getDworksApi(`/api/vendor/portal/erp/excel-imports${query ? `?${query}` : ""}`, {
    token,
    preferErrorDetail: true
  });
}

export async function commitVendorPortalExcelImport(token, payload) {
  requireVendorPortalToken(token);
  return postDworksApi("/api/vendor/portal/erp/excel-import/commit", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function rollbackVendorPortalExcelImport(token, batchId) {
  requireVendorPortalToken(token);
  const safeBatchId = encodeRequiredVendorPortalId(batchId, "되돌릴 Excel 업로드 정보가 없습니다.");
  return postDworksApi(`/api/vendor/portal/erp/excel-import/${safeBatchId}/rollback`, {}, {
    token,
    preferErrorDetail: true
  });
}

function requireScheduledOrderToken(token) {
  if (!token) {
    throw new Error("로그인 정보가 없습니다.");
  }
}

function encodeScheduledOrderId(scheduledOrderId) {
  const safeId = encodeURIComponent(String(scheduledOrderId || "").trim());
  if (!safeId) {
    throw new Error("예약발주 정보가 없습니다.");
  }
  return safeId;
}

// 예약발주 등록
export async function createScheduledOrder(token, payload) {
  requireScheduledOrderToken(token);
  return postDworksApi("/api/scheduled-orders", payload, {
    token,
    preferErrorDetail: true
  });
}

// 예약발주 목록 조회
export async function fetchScheduledOrders(token, options = {}) {
  requireScheduledOrderToken(token);
  const params = new URLSearchParams();
  if (options.status) params.set("status", String(options.status));
  if (options.limit) params.set("limit", String(options.limit));
  const query = params.toString();

  return getDworksApi(`/api/scheduled-orders${query ? `?${query}` : ""}`, {
    token,
    preferErrorDetail: true
  });
}

// 예약발주 일시·내용 수정
export async function updateScheduledOrder(token, scheduledOrderId, payload) {
  requireScheduledOrderToken(token);
  const safeId = encodeScheduledOrderId(scheduledOrderId);

  return requestDworksApi(`/api/scheduled-orders/${safeId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

// 예약발주 취소(데이터는 감사 로그와 상태 확인을 위해 보존)
export async function cancelScheduledOrder(token, scheduledOrderId, reason = "사용자 취소") {
  requireScheduledOrderToken(token);
  const safeId = encodeScheduledOrderId(scheduledOrderId);

  return requestDworksApi(`/api/scheduled-orders/${safeId}`, {
    method: "DELETE",
    payload: { reason },
    token,
    preferErrorDetail: true
  });
}


export async function createBuyerFactoryProductionOrder(token, payload) {
  if (!token) throw new Error("발주자 로그인 정보가 없습니다.");
  return postDworksApi("/api/buyer/factory-production-orders", payload, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchFactoryPortalMe(token) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  return getDworksApi("/api/factory/portal/me", { token, preferErrorDetail: true });
}

export async function fetchFactoryProductionOrders(token, options = {}) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  const params = new URLSearchParams();
  params.set("page", String(options.page || 1));
  params.set("per_page", String(options.perPage || 30));
  return getDworksApi(`/api/factory/portal/production-orders?${params.toString()}`, { token, preferErrorDetail: true });
}

export async function updateFactoryProductionOrder(token, orderId, payload) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  const safeOrderId = encodeURIComponent(String(orderId || "").trim());
  if (!safeOrderId) throw new Error("생산 요청 ID가 없습니다.");
  return requestDworksApi(`/api/factory/portal/production-orders/${safeOrderId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function updateFactoryProductionOrderItemPrice(token, itemId, payload) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  const safeItemId = encodeURIComponent(String(itemId || "").trim());
  if (!safeItemId) throw new Error("생산 품목 ID가 없습니다.");
  return requestDworksApi(`/api/factory/portal/production-order-items/${safeItemId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function fetchFactoryInventory(token) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  return getDworksApi("/api/factory/portal/inventory", { token, preferErrorDetail: true });
}

export async function updateFactoryInventory(token, inventoryId, payload) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  const safeId = encodeURIComponent(String(inventoryId || "").trim());
  if (!safeId) throw new Error("재고 ID가 없습니다.");
  return requestDworksApi(`/api/factory/portal/inventory/${safeId}`, {
    method: "PATCH",
    payload,
    token,
    preferErrorDetail: true
  });
}

export async function fetchFactoryDestinations(token) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  return getDworksApi("/api/factory/portal/destinations", { token, preferErrorDetail: true });
}

export async function fetchFactoryShipments(token, options = {}) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  const params = new URLSearchParams();
  params.set("page", String(options.page || 1));
  params.set("per_page", String(options.perPage || 30));
  return getDworksApi(`/api/factory/portal/shipments?${params.toString()}`, { token, preferErrorDetail: true });
}

export async function createFactoryShipment(token, payload) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  return postDworksApi("/api/factory/portal/shipments", payload, { token, preferErrorDetail: true });
}


export async function createFactoryInvite(token, payload) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  return postDworksApi("/api/buyer/factory-invites", payload, { token, preferErrorDetail: true });
}

export async function fetchFactoryInvite(inviteToken) {
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) throw new Error("공장 초대 정보가 없습니다.");
  return getDworksApi(`/api/factory/invites/${safeToken}`, { preferErrorDetail: true });
}

export async function joinFactoryInvite(token, inviteToken, payload) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  const safeToken = encodeURIComponent(String(inviteToken || "").trim());
  if (!safeToken) throw new Error("공장 초대 정보가 없습니다.");
  return postDworksApi(`/api/factory/invites/${safeToken}/join`, payload, {
    token,
    preferErrorDetail: true
  });
}




export async function fetchBuyerFactoryChats(token) {
  return getDworksApi("/api/buyer/factory-chat/threads", { token, preferErrorDetail: true });
}
export async function fetchBuyerFactoryOrderStatuses(token, orderId) {
  return getDworksApi(`/api/buyer/factory-production-orders/${encodeURIComponent(orderId)}/statuses`, {token, preferErrorDetail:true});
}
export async function fetchBuyerFactoryChat(token, threadId, limit=50) {
  return getDworksApi(`/api/buyer/factory-chat/threads/${encodeURIComponent(threadId)}?limit=${limit}`, { token, preferErrorDetail: true });
}
export async function sendBuyerFactoryChatMessage(token, threadId, payload) {
  return postDworksApi(`/api/buyer/factory-chat/threads/${encodeURIComponent(threadId)}/messages`, {
    body: payload?.body,
    images: payload?.images || [],
    clientMessageId: payload?.clientMessageId || (crypto.randomUUID ? crypto.randomUUID() : `buyer-factory-${Date.now()}`)
  }, { token, preferErrorDetail: true });
}

export async function fetchFactoryChatThreads(token) {
  if (!token) throw new Error("공장 로그인 정보가 없습니다.");
  return getDworksApi("/api/factory/portal/chat/threads", { token, preferErrorDetail: true });
}
export async function searchChatRooms(token, kind, { q, cursor }) {
  const sources = {
    buyer:{ vendor:"/api/buyer/chats/search", factory:"/api/buyer/factory-chat/threads/search" },
    vendor:{ vendor:"/api/vendor/portal/chats/search" },
    factory:{ factory:"/api/factory/portal/chat/threads/search" },
  }[kind];
  if (!token || !sources) throw new Error("채팅 로그인 정보가 없습니다.");
  const pages = await Promise.all(Object.entries(sources).map(async ([source, path]) => {
    if (cursor && cursor[source] === null) return [source, { threads:[], has_more:false }];
    const params = new URLSearchParams({ q, ...(cursor?.[source] || {}) });
    return [source, await getDworksApi(`${path}?${params}`, { token })];
  }));
  return {
    threads:pages.flatMap(([, page]) => page.threads || []),
    has_more:pages.some(([, page]) => page.has_more),
    cursor:Object.fromEntries(pages.map(([source, page]) => [source,
      page.has_more ? { offset:page.next_offset, snapshot:page.snapshot } : null])),
  };
}

export async function searchPortalChatMessages(token, kind, threadId, params) {
  if (!token || !threadId || !["buyer", "vendor", "factory"].includes(kind)) throw new Error("채팅방 로그인 정보가 없습니다.");
  const prefix = kind === "buyer" ? "/api/buyer/chats" : kind === "factory" ? "/api/factory/portal/chat/threads" : "/api/vendor/portal/chats";
  return getDworksApi(`${prefix}/${encodeURIComponent(threadId)}/search?${new URLSearchParams(params)}`, { token });
}

export async function fetchFactoryChatMessages(token, threadId, limit=50) {
  return getDworksApi(`/api/factory/portal/chat/threads/${encodeURIComponent(threadId)}/messages?limit=${limit}`, { token, preferErrorDetail: true });
}
export async function sendFactoryChatMessage(token, threadId, payload) {
  return postDworksApi(`/api/factory/portal/chat/threads/${encodeURIComponent(threadId)}/messages`, {
    body: payload?.body,
    images: payload?.images || [],
    clientMessageId: payload?.clientMessageId || (crypto.randomUUID ? crypto.randomUUID() : `factory-chat-${Date.now()}`)
  }, { token, preferErrorDetail: true });
}
export async function markFactoryChatRead(token, threadId) {
  return postDworksApi(`/api/factory/portal/chat/threads/${encodeURIComponent(threadId)}/read`, {}, { token, preferErrorDetail: true });
}
export async function fetchFactoryTeam(token) {
  return getDworksApi("/api/factory/portal/team", { token, preferErrorDetail: true });
}
export async function createFactoryTeamInvite(token, payload) {
  return postDworksApi("/api/factory/portal/team/invites", payload, { token, preferErrorDetail: true });
}
export async function joinFactoryTeam(token, inviteToken) {
  return postDworksApi(`/api/factory/portal/team/join/${encodeURIComponent(inviteToken)}`, {}, { token, preferErrorDetail: true });
}

function buildBuyerOrderQuery(params = {}) {
  const searchParams = new URLSearchParams();
  const allowed = {
    limit: params.limit,
    offset: params.offset,
    sort: params.sort,
    status: params.status,
    archived: params.archived,
    start_date: params.startDate || params.start_date,
    end_date: params.endDate || params.end_date,
    brand_client_id: params.brandClientId || params.brand_client_id,
    search: params.search
  };

  Object.entries(allowed).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export async function fetchBuyerOrdersPage(token, params = {}) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  return getDworksApi(`/api/buyer/orders${buildBuyerOrderQuery(params)}`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchBuyerOrderStats(token, params = {}) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  return getDworksApi(`/api/buyer/orders/stats${buildBuyerOrderQuery(params)}`, {
    token,
    preferErrorDetail: true
  });
}

export async function fetchBuyerDashboardStats(token, date) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  const params = new URLSearchParams();
  if (date) params.set("date", String(date));
  const query = params.toString();
  return getDworksApi(`/api/buyer/dashboard-stats${query ? `?${query}` : ""}`, {
    token,
    preferErrorDetail: true
  });
}


export async function fetchBuyerOrderDetail(token, orderId) {
  if (!token) throw new Error("로그인 정보가 없습니다.");
  const safeOrderId = encodeURIComponent(String(orderId || "").trim());
  if (!safeOrderId) throw new Error("발주 ID가 없습니다.");
  return getDworksApi(`/api/buyer/orders/${safeOrderId}`, {
    token,
    preferErrorDetail: true
  });
}


export function fetchBuyerPlanUsage(token) {
  return requestDworksApi("/api/buyer/plan-usage", {token});
}

// Shared operations inbox for vendor/factory portals; buyer ChatPage keeps its existing flow.
export function fetchOperationsBroadcasts(token) {
  return getDworksApi("/api/broadcast-chat", { token });
}

export function markOperationsBroadcastsRead(token) {
  return postDworksApi("/api/broadcast-chat/read-all", {}, { token });
}
